import { useLayoutEffect, useMemo } from 'react';
import type { Page } from '@rossum/api-client/pages';
import { type CameraSlice, createStoreHooks } from '../../../canvas/core';
import { useDocumentCanvasContext } from '../feature-extension/DocumentCanvasContext';
import type { LayoutBandHeights } from '../layout/calculate-layout';
import { createSelectDocumentLayout } from '../layout/select-document-layout';
import type { PageLayoutSlice } from '../store/types';
import { usePageVisibility } from './usePageVisibility';

type DocumentSetupDeps = CameraSlice & PageLayoutSlice;

const { useStore, useStoreApi } = createStoreHooks<DocumentSetupDeps>();

export const useDocumentSetup = (pages: Page[], bands?: LayoutBandHeights) => {
  const storeApi = useStoreApi();
  const initializePage = useStore(
    state => state.pageLayout.actions.initializePage
  );
  const setPageOrder = useStore(state => state.pageLayout.actions.setPageOrder);

  useLayoutEffect(() => {
    setPageOrder(pages.map(p => p.id));

    // Read pagesMap imperatively to avoid subscribing to it — each
    // initializePage call mutates the map, which would re-trigger this
    // effect N+1 times for N uninitialized pages.
    const currentPages = storeApi.getState().pageLayout.list;

    pages.forEach(page => {
      if (!currentPages[page.id]) {
        initializePage(page.id, { width: page.width, height: page.height });
      }
    });
  }, [pages, storeApi, initializePage, setPageOrder]);

  const pagesMap = useStore(state => state.pageLayout.list);
  const allPagesInitialized = pages.every(page => pagesMap[page.id]);

  const { getEffectiveDimensions } = useDocumentCanvasContext();
  const selectDocumentLayout = useMemo(
    () => createSelectDocumentLayout(getEffectiveDimensions, bands),
    [getEffectiveDimensions, bands]
  );
  const layout = useStore(selectDocumentLayout);
  const { pageLayouts, contentWidth, contentHeight, headerBand } = layout;

  usePageVisibility(pageLayouts);

  return {
    pageLayouts,
    contentWidth,
    contentHeight,
    headerBand,
    allPagesInitialized,
  };
};
